import type { apiTypes } from '@cmg/common';
import { Form, FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import usePrevious from '../../../../../../common/util/usePrevious';
import { ServerErrorAlert } from '../../../../../../design-system/alert/ServerErrorAlert';
import { PageGridSection } from '../../../../../../design-system/body-sections/PageGridSection';
import { PageGridSectionActions } from '../../../../../../design-system/body-sections/PageGridSectionActions';
import type {
  Saml2PConfigurationUpdate,
  Saml2PMetadata,
} from '../../../../../../types/domain/identity-provider/configurations/saml2p';
import type { IdentityProviderSaml } from '../../../../../../types/domain/identity-provider/identityProvider';
import { ProviderDetailsSectionEdit } from './ProviderDetailsSectionEdit';
import { ProviderDetailsSectionReadonly } from './ProviderDetailsSectionReadonly';
import type { SamlProviderFormValues } from './utils';
import { SamlUpdateSchema } from './utils';

export type Props = Readonly<{
  readonly canEdit: boolean;
  readonly provider: IdentityProviderSaml | null;
  readonly metadata: Saml2PMetadata | null;
  readonly error: apiTypes.GenericServerError | null;
  readonly isSubmitting: boolean;
  readonly onSubmit: (data: Saml2PConfigurationUpdate) => void;
  readonly onReset: () => void;
}>;

export const ProviderDetailsSection: FC<Props> = ({
  canEdit,
  provider,
  metadata,
  error,
  isSubmitting,
  onSubmit,
  onReset,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const formik = useFormik<SamlProviderFormValues>({
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema: SamlUpdateSchema,
    initialValues: {
      certificateFile: null,
      enabled: provider?.enabled ?? false,
      name: provider?.name ?? '',
      configuration: {
        entityId: provider?.configuration.entityId ?? '',
        signOnUrl: provider?.configuration.signOnUrl ?? '',
        logoutUrl: provider?.configuration.logoutUrl ?? '', // optional
        certificate: provider?.configuration.certificate ?? '',
      },
    },
    onSubmit: val => {
      onSubmit({
        enabled: val.enabled,
        name: val.name,
        configuration: {
          ...val.configuration,
          logoutUrl: val.configuration.logoutUrl || null, // the end point requires null if no value is provided
        },
      });
    },
  });

  const { resetForm } = formik;
  const previousIsEditing = usePrevious(isEditing);
  useEffect(() => {
    if (previousIsEditing && !isEditing) {
      resetForm();
      onReset();
    }
  }, [isEditing, onReset, previousIsEditing, resetForm]);

  const certificateMetadata = provider?.configuration.certificateMetadata ?? null;

  return (
    <FormikProvider value={formik}>
      <PageGridSection
        size="medium"
        title="Provider Details"
        FormComponent={Form}
        formComponentProps={{ noValidate: true }}
        actions={
          canEdit ? (
            <PageGridSectionActions
              isSubmitting={isSubmitting}
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onCancel={() => setIsEditing(false)}
              canEdit={canEdit}
            />
          ) : undefined
        }
        error={error && <ServerErrorAlert title="Error" error={error} />}
        content={
          isEditing ? (
            <ProviderDetailsSectionEdit
              metadata={metadata}
              certificateMetadata={certificateMetadata}
            />
          ) : (
            <ProviderDetailsSectionReadonly
              provider={provider}
              metadata={metadata}
              certificateMetadata={certificateMetadata}
            />
          )
        }
      />
    </FormikProvider>
  );
};
